// src/core/platform/capacitorBridge.ts
// Capacitor(Android) 환경 브릿지

import { Browser } from "@capacitor/browser"
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem"
import { App } from "@capacitor/app"
import { registerPlugin } from "@capacitor/core"
import { fetchWithTimeout } from "../utils/fetchWithTimeout"
import type { PlatformBridge, PlatformFeature, SaveDialogOptions, SaveDialogResult } from "./platformBridge"

/** 네이티브 TCP 스캐너 플러그인 인터페이스 */
interface TcpScannerPlugin {
  scanSubnet(options: { subnet: string; port: number; timeout: number }): Promise<{
    servers: string[]
    port: number
    subnet: string
  }>
  checkPort(options: { ip: string; port: number; timeout: number }): Promise<{
    ip: string
    port: number
    open: boolean
    error?: string
  }>
  checkTlsCertificate(options: { ip: string; port: number; timeout: number }): Promise<{
    ip: string
    port: number
    certificatePresent: boolean
    ipCovered: boolean
    error?: string
  }>
  getLocalIps(): Promise<{
    ips: string[]
  }>
}

const TcpScanner = registerPlugin<TcpScannerPlugin>("TcpScanner")

const DEFAULT_SCAN_SUBNETS = [
  "192.168.0",
  "192.168.1",
  "192.168.10",
  "192.168.100",
  "192.168.219",
  "10.0.0",
  "10.0.1",
  "10.0.10",
  "172.16.0",
  "172.16.1",
]
const HTTP_FALLBACK_SCAN_LIMIT = 2

interface ScanSubnetPlan {
  primarySubnets: string[]
  fallbackSubnets: string[]
  isEmulator: boolean
}

const isPrivateIpv4 = (value?: string | null): boolean => {
  if (!value) {
    return false
  }

  const parts = value
    .trim()
    .split(".")
    .map(part => Number(part))

  if (parts.length !== 4 || parts.some(part => !Number.isInteger(part) || part < 0 || part > 255)) {
    return false
  }

  const [first, second] = parts
  return first === 10 || (first === 172 && second >= 16 && second <= 31) || (first === 192 && second === 168)
}

const normalizePrivateIps = (values: string[]): string[] => {
  const seenIps = new Set<string>()
  const normalizedIps: string[] = []

  values.forEach(value => {
    const ip = value.trim()
    if (!isPrivateIpv4(ip) || seenIps.has(ip)) {
      return
    }

    seenIps.add(ip)
    normalizedIps.push(ip)
  })

  return normalizedIps
}

const normalizeSubnet = (value?: string | null): string | null => {
  if (!value) {
    return null
  }

  const parts = value
    .trim()
    .split(".")
    .map(part => Number(part))

  if (parts.length !== 3 || parts.some(part => !Number.isInteger(part) || part < 0 || part > 255)) {
    return null
  }

  return parts.join(".")
}

const getSubnetFromIp = (value?: string | null): string | null => {
  if (!value) {
    return null
  }

  const parts = value
    .trim()
    .split(".")
    .map(part => Number(part))

  if (parts.length !== 4 || parts.some(part => !Number.isInteger(part) || part < 0 || part > 255)) {
    return null
  }

  return normalizeSubnet(parts.slice(0, 3).join("."))
}

/** Electron 전용 기능 — Android에서 비활성화 */
const ELECTRON_ONLY_FEATURES: PlatformFeature[] = [
  "npki-cert",
  "hira-iea",
  "cert-agent",
  "raw-print",
  "screen-capture",
  "chat-window",
  "window-control",
  "edi-file",
  "remote-support",
  "zoom-control",
  "image-viewer",
]

export class CapacitorBridge implements PlatformBridge {
  readonly platform = "capacitor" as const

  private getStoredServerIp(): string | null {
    if (typeof window === "undefined") {
      return null
    }

    const storedServerIp = localStorage.getItem("SigmaServerIp")
    const normalizedSubnet = getSubnetFromIp(storedServerIp)
    return normalizedSubnet ? storedServerIp : null
  }

  private buildScanSubnets(localIps: string[]): ScanSubnetPlan {
    const primarySubnets: string[] = []
    const fallbackSubnets: string[] = []
    const seenSubnets = new Set<string>()
    const storedServerIp = this.getStoredServerIp()
    const isEmulator = localIps.some(ip => ip.startsWith("10.0.2."))

    const pushPrimarySubnet = (candidate?: string | null) => {
      const normalizedSubnet = normalizeSubnet(candidate)
      if (!normalizedSubnet || seenSubnets.has(normalizedSubnet)) {
        return
      }

      seenSubnets.add(normalizedSubnet)
      primarySubnets.push(normalizedSubnet)
    }

    const pushFallbackSubnet = (candidate?: string | null) => {
      const normalizedSubnet = normalizeSubnet(candidate)
      if (!normalizedSubnet || seenSubnets.has(normalizedSubnet)) {
        return
      }

      seenSubnets.add(normalizedSubnet)
      fallbackSubnets.push(normalizedSubnet)
    }

    localIps.forEach(ip => {
      pushPrimarySubnet(getSubnetFromIp(ip))
    })
    pushPrimarySubnet(getSubnetFromIp(storedServerIp))

    DEFAULT_SCAN_SUBNETS.forEach(pushFallbackSubnet)

    return {
      primarySubnets,
      fallbackSubnets,
      isEmulator,
    }
  }

  private async getNativeLocalIps(): Promise<string[]> {
    try {
      const result = await TcpScanner.getLocalIps()
      return normalizePrivateIps(result.ips || [])
    } catch (error) {
      console.warn("[CapacitorBridge] 네이티브 로컬 IP 수집 실패", error)
      return []
    }
  }

  private async getWebRtcLocalIp(): Promise<string | null> {
    return new Promise(resolve => {
      try {
        const pc = new RTCPeerConnection({ iceServers: [] })
        pc.createDataChannel("")
        pc.createOffer()
          .then(offer => pc.setLocalDescription(offer))
          .catch(() => resolve(null))

        const timeout = setTimeout(() => {
          pc.close()
          resolve(null)
        }, 3000)

        pc.onicecandidate = event => {
          if (!event.candidate) return
          const match = event.candidate.candidate.match(/(\d{1,3}\.){3}\d{1,3}/)
          if (match) {
            const ip = match[0]
            if (isPrivateIpv4(ip)) {
              clearTimeout(timeout)
              pc.close()
              resolve(ip)
            }
          }
        }
      } catch {
        resolve(null)
      }
    })
  }

  private async collectLocalIps(): Promise<string[]> {
    const nativeIps = await this.getNativeLocalIps()
    if (nativeIps.length > 0) {
      return nativeIps
    }

    const fallbackIp = await this.getWebRtcLocalIp()
    return fallbackIp ? [fallbackIp] : []
  }

  isFeatureAvailable(feature: PlatformFeature): boolean {
    return !ELECTRON_ONLY_FEATURES.includes(feature)
  }

  // ── 파일 관련 ──

  async showSaveDialog(_options?: SaveDialogOptions): Promise<SaveDialogResult> {
    // Android에서는 네이티브 저장 다이얼로그 대신 직접 저장
    // 호출측에서 writeBinaryFile/writeFile 사용 권장
    return { canceled: false, filePath: undefined }
  }

  async writeFile(path: string, content: string) {
    try {
      await Filesystem.writeFile({
        path,
        data: content,
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
        recursive: true,
      })
      return { success: true }
    } catch (e) {
      return { success: false, error: String(e) }
    }
  }

  async writeBinaryFile(path: string, data: Uint8Array | ArrayBuffer) {
    try {
      // Capacitor Filesystem은 base64 문자열로 바이너리 처리
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data)
      const base64 = btoa(String.fromCharCode(...bytes))
      await Filesystem.writeFile({
        path,
        data: base64,
        directory: Directory.Documents,
        recursive: true,
      })
      return { success: true }
    } catch (e) {
      return { success: false, error: String(e) }
    }
  }

  async openFileLocation(_filePath: string) {
    return { success: false, error: "Capacitor 환경에서 폴더 열기 미지원" }
  }

  // ── 앱 제어 ──

  async openExternalUrl(url: string) {
    await Browser.open({ url })
  }

  async restartApp() {
    // Android에서는 앱 종료 후 시스템이 재시작 처리
    await App.exitApp()
  }

  async exitApp() {
    await App.exitApp()
  }

  // ── 서버 검색 ──

  /**
   * Android 서브넷 IP 스캔 — 네이티브 TCP 소켓 사용 (Electron net.Socket과 동일 방식)
   * Java ExecutorService로 254개 TCP 소켓을 동시에 열어 포트가 열린 서버를 찾는다.
   * HTTP/SSL 오버헤드 없이 순수 TCP connect만 사용하므로 ~300ms 이내에 완료.
   */
  async scanNetwork() {
    const port = 57443
    const results: Array<{ ip: string; port: number; protocol: string }> = []
    const seenIps = new Set<string>()
    const storedServerIp = this.getStoredServerIp()
    let httpFallbackCount = 0

    const pushResult = (ip: string) => {
      if (seenIps.has(ip)) return
      seenIps.add(ip)
      results.push({ ip, port, protocol: "https" })
    }

    const scanSubnets = async (subnets: string[], stopOnFirstHit: boolean) => {
      for (const subnet of subnets) {
        console.log(`[CapacitorBridge] ${subnet}.* 네이티브 TCP 스캔...`)
        try {
          const result = await TcpScanner.scanSubnet({ subnet, port, timeout: 300 })
          console.log(`[CapacitorBridge] TCP 스캔 결과:`, JSON.stringify(result))
          if (result.servers && result.servers.length > 0) {
            for (const ip of result.servers) {
              pushResult(ip)
            }
          }
        } catch (err) {
          console.error(`[CapacitorBridge] TCP 스캔 실패:`, err)
          if (httpFallbackCount < HTTP_FALLBACK_SCAN_LIMIT) {
            httpFallbackCount += 1
            console.log(`[CapacitorBridge] HTTP 폴백 스캔으로 전환...`)
            await this.httpFallbackScan(subnet, port, results)
          } else {
            console.warn(`[CapacitorBridge] HTTP 폴백 생략: 과도한 서브넷 스캔 방지 (${subnet}.*)`)
          }
        }

        if (stopOnFirstHit && results.length > 0) {
          return
        }
      }
    }

    console.log("[CapacitorBridge] scanNetwork 시작 (네이티브 TCP 스캐너)")
    const localIps = await this.collectLocalIps()
    console.log("[CapacitorBridge] 감지된 로컬 IP 목록:", localIps)
    const primaryLocalIp = localIps[0] || null
    const { primarySubnets, fallbackSubnets, isEmulator } = this.buildScanSubnets(localIps)

    if (storedServerIp) {
      const storedServerAvailable = await this.checkServer(storedServerIp, port)
      if (storedServerAvailable) {
        pushResult(storedServerIp)
        console.log("[CapacitorBridge] ✅ 저장된 서버 재발견:", storedServerIp)
      }
    }

    // Android Emulator는 10.0.2.x NAT 네트워크를 사용하며 호스트는 10.0.2.2로 접근 가능
    // 에뮬레이터 환경에서는 먼저 10.0.2.2를 우선 체크한다.
    // 다만 여기서 바로 반환하면 실제 LAN(192.168.x.x) 서버 검색이 막히므로,
    // 호스트 별칭을 결과에 추가만 하고 아래 서브넷 스캔은 계속 진행한다.
    if (isEmulator) {
      console.log("[CapacitorBridge] 에뮬레이터 환경 감지: 10.0.2.2 우선 확인")
      const hostOk = await this.checkServer("10.0.2.2", port)
      if (hostOk) {
        pushResult("10.0.2.2")
        console.log("[CapacitorBridge] ✅ 에뮬레이터 호스트 서버 발견: 10.0.2.2")
      }
    }

    console.log("[CapacitorBridge] 대표 로컬 IP:", primaryLocalIp)
    console.log("[CapacitorBridge] 1차 스캔 서브넷:", primarySubnets)
    await scanSubnets(primarySubnets, false)

    if (results.length === 0 || isEmulator) {
      console.log("[CapacitorBridge] fallback 스캔 서브넷:", fallbackSubnets)
      await scanSubnets(fallbackSubnets, !isEmulator)
    }

    console.log("[CapacitorBridge] scanNetwork 완료, 결과:", results.length, "개")
    return results
  }

  /** HTTP 폴백 스캔 — 네이티브 플러그인 사용 불가 시 */
  private async httpFallbackScan(subnet: string, port: number, results: Array<{ ip: string; port: number; protocol: string }>) {
    const ips = Array.from({ length: 254 }, (_, i) => `${subnet}.${i + 1}`)
    const checks = ips.map(async ip => {
      const ok = await this.checkServerHttp(ip, port)
      if (ok) {
        console.log(`[CapacitorBridge] ✅ 서버 발견 (HTTP): ${ip}:${port}`)
        results.push({ ip, port, protocol: "https" })
      }
    })
    await Promise.all(checks)
  }

  async getLocalIp(): Promise<string | null> {
    const localIps = await this.collectLocalIps()
    return localIps[0] || null
  }

  async checkServer(ip: string, port: number) {
    // 네이티브 TCP 소켓으로 포트 오픈 여부 확인
    try {
      const result = await TcpScanner.checkPort({ ip, port, timeout: 500 })
      return result.open
    } catch {
      // 네이티브 플러그인 실패 시 HTTP 폴백
      return this.checkServerHttp(ip, port)
    }
  }

  async diagnoseTlsCertificate(ip: string, port: number) {
    try {
      return await TcpScanner.checkTlsCertificate({ ip, port, timeout: 3000 })
    } catch (error) {
      return {
        certificatePresent: false,
        ipCovered: false,
        error: error instanceof Error ? error.message : String(error),
      }
    }
  }

  /** HTTP GET 폴백 — 네이티브 TCP 플러그인 사용 불가 시 */
  private async checkServerHttp(ip: string, port: number) {
    try {
      const protocol = port === 443 || port === 57443 ? "https" : "http"
      const response = await fetchWithTimeout(
        `${protocol}://${ip}:${port}/`,
        {
          method: "GET",
        },
        800
      )
      return response.status > 0
    } catch {
      return false
    }
  }

  async saveProxyConfig(_config: unknown) {
    // Android에서는 localStorage에 저장
    if (_config && typeof _config === "object") {
      localStorage.setItem("SigmaServerConfig", JSON.stringify(_config))
    }
  }
}

